import React, {
	useCallback,
	useState,
	useMemo,
	useLayoutEffect,
	memo,
} from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { isUndefined } from "lodash";

import { RoundingMethod } from "../../../../../../../../../redux/constants/OrdersPage/order";
import useObjectEditor from "../../../../../../../../../hooks/useObjectEditor";
import { EditIcon } from "../../../../../../../../../icons/edit";
import {
	NumberInput,
	NumberInputMemo,
} from "../../../../../../../../../components/Orders";
import { useCurrencyGlobalSettings } from "../../../../../../../../../hooks";
import { StyledRow } from "../../../../../../../../../components/common";
import { OrderFormProps } from "../../../types/FormProps";
import { Gray, Primary } from "../../styled";

import Clickable from "./Clickable";

interface Props extends OrderFormProps {
	edit: boolean;
	setEdit: (edit: boolean) => void;
}

const AdditionalCost: React.FC<Props> = ({
	disabled,
	edit,
	setEdit,
	form,
	tab,
}) => {
	const { t } = useTranslation();
	const currencyGlobalSettings = useCurrencyGlobalSettings();

	const [value, onChangeValue] = useState<NumberInput.Value>({
		action: "add",
		value: 0,
	});

	// Live form values: in the archive edit `tab.form` is a snapshot of the
	// opened order, so a second change would start from old numbers (see
	// `Price.tsx`).
	const livePrice = useWatch({ control: form.control, name: "price" });
	const liveAdditionalCost = useWatch({
		control: form.control,
		name: "additionalCost",
	});
	const watchedPrice = useMemo(
		() => livePrice ?? tab.form?.price ?? 0,
		[livePrice, tab.form?.price],
	);

	const roundingMultipleValue = useMemo(
		() => tab.form?.priceSettings?.rounding?.multiple?.value ?? 0,
		[tab.form?.priceSettings?.rounding?.multiple?.value],
	);
	const roundingMultipleActive = useMemo(
		() => tab.form?.priceSettings?.rounding?.multiple?.active ?? false,
		[tab.form?.priceSettings?.rounding?.multiple?.active],
	);
	const roundingMethod = useMemo(
		() =>
			tab.form?.priceSettings?.rounding?.method ?? RoundingMethod.GENERAL,
		[tab.form?.priceSettings?.rounding?.method],
	);

	const precision = useMemo(
		() => tab.form?.priceSettings?.rounding?.precision ?? 2,
		[tab.form?.priceSettings?.rounding?.precision],
	);

	const additionalCost = useMemo(
		() => liveAdditionalCost ?? tab.form.additionalCost ?? 0,
		[liveAdditionalCost, tab.form.additionalCost],
	);

	// A taximeter order is not recalculated on the fly (the server keeps the
	// taximeter price), so the surcharge goes into the shown price here, the
	// way the save folds it: to the kopeck and never below zero (dev check
	// F14, `foldArchiveTaximeterPrice`).
	const foldIntoPrice = !!tab.form?.processedByTaximeter;

	const valueEditor = useObjectEditor(value, onChangeValue);

	const setAction = valueEditor.useSetter("action");
	const valueCost = valueEditor.useGetter("value");
	const setValue = valueEditor.useSetter("value");

	const stringifyPrice = useCallback(
		(price: number | undefined) => {
			if (isUndefined(price)) return "-";

			let processedPrice = price;

			if (roundingMultipleActive) {
				processedPrice /= roundingMultipleValue;
			} else {
				processedPrice *= 10 ** precision;
			}

			switch (roundingMethod) {
				case RoundingMethod.GENERAL:
					processedPrice = Math.round(processedPrice);
					break;
				case RoundingMethod.CEIL:
					processedPrice = Math.ceil(processedPrice);
					break;
				case RoundingMethod.FLOOR:
					processedPrice = Math.floor(processedPrice);
					break;
				default:
					break;
			}

			if (roundingMultipleActive) {
				processedPrice *= roundingMultipleValue;
			} else {
				processedPrice /= 10 ** precision;
			}

			return `${processedPrice.toFixed(precision)}`.trim();
		},
		[
			precision,
			roundingMethod,
			roundingMultipleActive,
			roundingMultipleValue,
		],
	);

	useLayoutEffect(() => {
		if (valueCost !== additionalCost) setValue(additionalCost);
	}, [additionalCost, setValue, valueCost]);

	const onChange = useCallback(
		(data: NumberInput.Value) => {
			setValue(data.value);
			setAction(data.action);
			const dataValue = data.value;
			const cost = dataValue + Number(additionalCost);
			const prevPrice = Number(watchedPrice) - Number(additionalCost);
			const payload = Number(additionalCost) + dataValue;
			const applyAdditionalCost = (newAdditionalCost: number) => {
				form.setValue("additionalCost", newAdditionalCost);
				if (foldIntoPrice) {
					form.setValue(
						"price",
						Math.max(
							0,
							Math.round((prevPrice + newAdditionalCost) * 100) /
								100,
						),
					);
				}
			};

			if (data.action === "set") {
				// The new surcharge replaces the current one.
				if (dataValue + prevPrice < 0) {
					applyAdditionalCost(-Number(stringifyPrice(prevPrice)));
					setEdit(false);
					return;
				}

				applyAdditionalCost(dataValue);
			} else {
				if (cost + prevPrice <= 0) {
					applyAdditionalCost(-Number(stringifyPrice(prevPrice)));
					setEdit(false);
					return;
				}

				applyAdditionalCost(payload);
			}
			setEdit(false);
		},
		[
			setValue,
			setAction,
			additionalCost,
			watchedPrice,
			setEdit,
			form,
			stringifyPrice,
			foldIntoPrice,
		],
	);

	return (
		<StyledRow alignItems="end" gap="0 6px">
			<Primary>
				{`${t([
					`orderPage.order_form.additional_cost`,
					"Additional cost",
				])}:`}
			</Primary>
			<Gray style={{ whiteSpace: "nowrap" }}>
				{`${stringifyPrice(additionalCost)} ${currencyGlobalSettings}`}
			</Gray>

			{!disabled && (
				<Clickable onClick={() => setEdit(true)}>
					<EditIcon />
				</Clickable>
			)}

			{edit && (
				<NumberInputMemo
					shadow
					title={
						t([
							`orderPage.order_form.additional_cost`,
							"Additional cost",
						]) ?? ""
					}
					unit={currencyGlobalSettings ?? ""}
					value={value}
					onSubmit={onChange}
					onClose={() => setEdit(false)}
				/>
			)}
		</StyledRow>
	);
};

export default memo(AdditionalCost);
